import { useApiMutation } from "@gladis/api-client";
import { useState, type ReactNode } from "react";
import { Button, SelectField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

export interface TransferCandidate {
  userId: string;
  displayName: string;
}

/**
 * Ownership transfer (AC-H7).
 *
 * Two-step, for the same reason `DeleteAccountDialog` is: the consequence is large and one click is
 * the wrong affordance for it. The confirmation states BOTH consequences, because only one of them
 * is obvious — people expect the recipient to gain something, and are surprised to find they
 * themselves become an Admin and lose the ability to delete the household.
 *
 * Only current Admins are offered. That is the interface half of FR-015; the server half is
 * `MembershipService.canTransferTo`, which refuses anyone else regardless of what was rendered — so
 * a stale list cannot become a privilege escalation, only a failed request.
 */
export function TransferOwnershipDialog({
  householdId,
  householdName,
  candidates,
  onTransferred,
}: {
  householdId: string;
  householdName: string;
  candidates: readonly TransferCandidate[];
  onTransferred: () => void;
}): ReactNode {
  const t = useT();
  const [selected, setSelected] = useState<string>("");
  const [confirming, setConfirming] = useState(false);

  const transfer = useApiMutation<void, { toUserId: string }>(
    (client, body) => client.post(`/households/${householdId}/transfer-ownership`, body),
    {
      // BOTH keys, and `me` is the one that is easy to forget: a transfer changes the ACTING user's
      // own role, so every control gated on `isOwner` — Transfer, Delete, Leave — is wrong until
      // `me` is refetched. Invalidating only the member list leaves the page showing the previous
      // Owner as Owner until they happen to reload, which is exactly long enough to be confusing.
      invalidates: [["me"], ["members", householdId]],
      onSuccess: onTransferred,
    },
  );

  const chosen = candidates.find((candidate) => candidate.userId === selected);

  // An Owner alone in their household has nobody to transfer to. Saying so is more useful than a
  // disabled control with no explanation — and it is the moment to point at the only other way out.
  if (candidates.length === 0) {
    return (
      <section className="mt-10 rounded-control border border-line bg-panel p-5">
        <h2 className="text-xl font-semibold">{t("identity.transfer.heading")}</h2>
        <p className="mt-2 text-muted">
          There is nobody to transfer {householdName} to — you are its only member. Invite an admin
          first, or delete the household.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.transfer.heading")}</h2>

      {confirming && chosen ? (
        <div className="mt-4" role="alert">
          <p>
            <strong>{chosen.displayName}</strong> will become the owner of {householdName}. They
            will be able to delete the household.
          </p>
          <p className="mt-2 text-muted">{t("identity.transfer.consequence")}</p>

          <div className="mt-4 flex gap-3">
            <Button
              variant="primary"
              type="button"
              onClick={() => transfer.mutate({ toUserId: chosen.userId })}
              disabled={transfer.isPending}
            >
              {transfer.isPending
                ? t("identity.transfer.transferring")
                : `Transfer to ${chosen.displayName}`}
            </Button>
            <Button type="button" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <p className="text-muted">{t("identity.transfer.hint")}</p>

          <label className="mt-3 block" htmlFor="transfer-to">
            {t("identity.transfer.newOwner")}
          </label>
          <SelectField
            id="transfer-to"
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          >
            <option value="">{t("identity.transfer.choose")}</option>
            {candidates.map((candidate) => (
              <option key={candidate.userId} value={candidate.userId}>
                {candidate.displayName}
              </option>
            ))}
          </SelectField>

          <Button
            className="mt-4"
            type="button"
            disabled={selected === ""}
            onClick={() => setConfirming(true)}
          >
            {t("common.continue")}
          </Button>
        </div>
      )}

      {transfer.error ? (
        <p className="mt-3 text-muted" role="alert">
          {errorSentence(transfer.error)}
        </p>
      ) : null}
    </section>
  );
}
